import React, { ChangeEvent, useState } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Link } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import logo from '/src/assets/images/Logo/Logos.png?url';
import { blue } from '@mui/material/colors';
import ArrowForwardIcon from '@mui/icons-material/ArrowCircleLeft';

import * as S from './styles';
import Texts from '../../Texts';
import { validationSchema } from './validation';
import {
  sendMobileNumber,
  setRecaptchaToken,
  setIsRegistered,
} from '../../../Slice/auth/authSlice';
import { RootState, AppDispatch } from '../../../store';
import NumberConverter from '../../../helper/ConvertPersionNumberToEnglish';
import GoogleRecaptchaComponent from './GoogleRecaptchaComponent/GoogleRecaptchaComponent';

const RECAPTCHA_ENABLED = import.meta.env.VITE_RECAPTCHA_ENABLED === 'true';
import SupportModal from './SupportModal';

/** An Iranian mobile number: 11 digits, always starting 09. */
const MOBILE_LENGTH = 11;
const MOBILE_PREFIX_FA = '۰۹';

const RegisterBox: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, recaptchaToken } = useSelector(
    (state: RootState) => state.auth,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formik = useFormik({
    // Empty, not '۰۹': a prefilled prefix made the field look valid-in-progress
    // and suppressed the "required" message on an untouched form.
    initialValues: { mobile: '' },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const englishMobile = NumberConverter.toEnglishNumber(values.mobile);
      dispatch(
        sendMobileNumber({ mobile: englishMobile, recaptchaToken }),
      ).then((result) => {
        if (result.meta.requestStatus === 'fulfilled') {
          dispatch(setIsRegistered(true));
        }
      });
    },
  });

  const handleMobileChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Digits only, in any script, capped at the 11 a mobile number has.
    //
    // The mask keeps a leading "09" visible, but the value is still stored in
    // full so yup validates what the user really has: an earlier version
    // dropped anything not starting with 09 before it reached state, so the
    // error message could never appear.
    const digits = NumberConverter.toEnglishNumber(event.target.value)
      .replace(/\D/g, '')
      .slice(0, MOBILE_LENGTH);

    formik.setFieldValue(
      'mobile',
      digits ? NumberConverter.toPersianNumber(digits) : '',
    );
  };

  // Re-apply the prefix the mask promises. Typing into an empty field starts
  // the number at 09 rather than making the user type it.
  const handleMobileFocus = () => {
    if (!formik.values.mobile) {
      formik.setFieldValue('mobile', MOBILE_PREFIX_FA);
    }
  };

  // Leaving the field with nothing but the prefix counts as empty, so the
  // "required" rule still fires instead of failing the pattern check.
  const handleMobileBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (formik.values.mobile === MOBILE_PREFIX_FA) {
      formik.setFieldValue('mobile', '');
    }
    formik.handleBlur(event);
  };

  const handleVerificationResult = (token: string | null) => {
    dispatch(setRecaptchaToken(token));
  };

  return (
    <>
      {/* Above the card, not inside it, so the brand reads as the page's own
          rather than as the first field of the form. */}
      <Box
        component="img"
        src={logo}
        alt="سیبچه"
        sx={{
          display: 'block',
          width: '150px',
          height: 'auto',
          margin: '0 auto 24px',
        }}
      />

      <S.SBox>
        <S.Form onSubmit={formik.handleSubmit}>
        {/* Top-right of the card, deliberately low contrast: it is a way out,
            not one of the form's actions. */}
        <Link
          href="https://sibche.com"
          underline="hover"
          sx={{
            // The form column is LTR-ordered, so flex-end is its right edge.
            alignSelf: 'flex-end',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            fontFamily: 'IRANYekanXFaNum',
            fontSize: 11,
            color: '#A6AAB5',
            // Pulled up towards the card's top edge (the form has a 4px gap
            // between children) and given room before the title below.
            marginTop: '-10px',
            marginBottom: '12px',
            '&:hover': { color: '#60646E' },
          }}
        >
          {Texts.header.returnSibche}
          {/* After the text, so it sits on its right in this RTL line, and
              flipped to point right — the "back" direction when reading RTL. */}
          <ArrowBackIcon sx={{ fontSize: 12, transform: 'scaleX(-1)' }} />
        </Link>

        <S.TitlePhone as="div">{Texts.Register.title}</S.TitlePhone>
        <S.Label>{Texts.Register.inputMobile}</S.Label>
        <S.TextFieldWrapper>
          <S.TextFieldBackground />
          <S.STextField
            id="mobile"
            name="mobile"
            type="text"
            placeholder="۰۹xxxxxxxx"
            value={formik.values.mobile}
            onChange={handleMobileChange}
            onFocus={handleMobileFocus}
            onBlur={handleMobileBlur}
            inputProps={{
              maxLength: MOBILE_LENGTH,
              inputMode: 'numeric',
              dir: 'ltr',
            }}
            error={formik.touched.mobile && Boolean(formik.errors.mobile)}
            helperText={
              formik.touched.mobile && formik.errors.mobile ? (
                <S.CustomHelperText error>
                  {formik.errors.mobile}
                </S.CustomHelperText>
              ) : null
            }
          />
        </S.TextFieldWrapper>
        <S.ButtonContainer>
          <Button
            variant="contained"
            type="submit"
            disabled={loading || (RECAPTCHA_ENABLED && !recaptchaToken)}
            startIcon={<ArrowForwardIcon />}
            sx={{
              width: '100%',
              height: '44px',
              backgroundColor: '#2C3EDB',
              color: '#FFFFFF',
              fontSize: '14px',
              fontFamily: 'IRANYekanXFaNum',
              fontWeight: 700,
              lineHeight: '16px',
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#2333B8' },
            }}
          >
            {loading ? 'در حال ارسال...' : Texts.Register.buttonRegister}
          </Button>
        </S.ButtonContainer>
        {error && (
          <S.ErrorMessage>
            {typeof error === 'string' ? error : 'خطایی رخ داده است'}
          </S.ErrorMessage>
        )}
        {RECAPTCHA_ENABLED && !recaptchaToken && (
          <S.ErrorMessage>در حال بارگذاری reCAPTCHA...</S.ErrorMessage>
        )}
        {RECAPTCHA_ENABLED && (
          <GoogleRecaptchaComponent
            onVerificationResult={handleVerificationResult}
          />
        )}
        <S.Divider />
        <S.LinkContainer>
          <S.ClickableTextBlue onClick={() => setIsModalOpen(true)}>
            {Texts.Register.support}
          </S.ClickableTextBlue>
          {/* Plain label, not a control: only "تماس با پشتیبانی" beside it
              opens the modal. */}
          <S.StaticText>{Texts.Register.helpMe}</S.StaticText>
          </S.LinkContainer>
        </S.Form>
        <SupportModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </S.SBox>
    </>
  );
};

export default RegisterBox;
